/**
 * SettingsView
 * User settings including profile and SharpSports sportsbook linking
 */

import { LegalModal } from '@components/ui/LegalModal';
import UpgradeModal from '@components/ui/UpgradeModal';
import { useAuth } from '@contexts/AuthContext';
import { useTheme } from '@contexts/ThemeContext';
import { useFeatureAccess } from '@hooks/useFeatureAccess';
import { AUTH_OPERATION_FALLBACKS } from '@parlay-picker/shared/authMessages';
import { cn } from '@utils/cn';
import { motion } from 'framer-motion';
import { BookOpen, ChevronRight, ExternalLink, HelpCircle, Lock, X } from 'lucide-react';
import { type ReactElement, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getThemeList } from '../../config/themes';
import { AccountDeletionSection } from './components/AccountDeletionSection';
import { AccountPauseSection } from './components/AccountPauseSection';
import { AppearanceSection } from './components/AppearanceSection';
import { BlockedUsersSection } from './components/BlockedUsersSection';
import { CreatorEarningsSection } from './components/CreatorEarningsSection';
import { LineShoppingSection } from './components/LineShoppingSection';
import { NotificationsSection } from './components/NotificationsSection';
import { PicksSection } from './components/PicksSection';
import { ProfileSection } from './components/ProfileSection';
import { QuotasSection } from './components/QuotasSection';
import { RelationshipPermissionsSection } from './components/RelationshipPermissionsSection';
import { SecuritySection } from './components/SecuritySection';
import { SharpSportsSection } from './components/SharpSportsSection';
import { SubscriptionSection } from './components/SubscriptionSection';

// =============================================================================
// Main Component
// =============================================================================

interface SettingsViewProps {
  onClose: () => void;
  onOpenEarningsDashboard: () => void;
}

export function SettingsView({
  onClose,
  onOpenEarningsDashboard,
}: SettingsViewProps): ReactElement {
  const {
    user,
    updateProfile,
    changePassword,
    updatePreferences,
    requestEmailChange,
    cancelEmailChange,
  } = useAuth();
  const { themeId, setTheme } = useTheme();
  const { canUseTheme, isLocked, isFree, isPro, isPremium, isElite } = useFeatureAccess();
  const themes = getThemeList();
  const [showThemeUpgrade, setShowThemeUpgrade] = useState(false);
  const [showSubscriptionUpgrade, setShowSubscriptionUpgrade] = useState(false);
  const [legalModalType, setLegalModalType] = useState<'terms' | 'privacy' | null>(null);

  const [actionMessage, setActionMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);

  // Handle ?connect=return from Stripe onboarding redirect (mount-only)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connectParam = params.get('connect');
    if (connectParam === 'return') {
      setActionMessage({
        type: 'success',
        text: 'Stripe Connect setup complete! Your account status will update shortly.',
      });
      params.delete('connect');
      window.history.replaceState(
        {},
        '',
        `${window.location.pathname}${params.size ? `?${params}` : ''}`,
      );
    } else if (connectParam === 'refresh') {
      setActionMessage({
        type: 'error',
        text: 'Stripe onboarding was not completed. Please try again.',
      });
      params.delete('connect');
      window.history.replaceState(
        {},
        '',
        `${window.location.pathname}${params.size ? `?${params}` : ''}`,
      );
    }
  }, []);

  // Handler: Save profile (displayName, firstName, lastName)
  const handleSaveProfile = useCallback(
    async (profile: {
      displayName?: string;
      firstName?: string;
      lastName?: string;
    }): Promise<void> => {
      try {
        await updateProfile(profile);
        setActionMessage({ type: 'success', text: 'Profile updated successfully' });
      } catch (err) {
        setActionMessage({
          type: 'error',
          text: err instanceof Error ? err.message : AUTH_OPERATION_FALLBACKS.updateProfile,
        });
        // Rethrown so ProfileSection keeps the editor open with what was typed.
        throw err;
      }
    },
    [updateProfile],
  );

  // Handler: Change password
  const handleChangePassword = useCallback(
    async (current: string, newPassword: string, confirmPassword: string): Promise<void> => {
      if (newPassword !== confirmPassword) {
        setActionMessage({ type: 'error', text: 'New passwords do not match' });
        throw new Error('New passwords do not match');
      }

      if (newPassword.length < 8) {
        setActionMessage({ type: 'error', text: 'Password must be at least 8 characters' });
        throw new Error('Password must be at least 8 characters');
      }

      try {
        await changePassword(current, newPassword);
        setActionMessage({ type: 'success', text: 'Password changed successfully' });
      } catch (err) {
        setActionMessage({
          type: 'error',
          text: (err as Error).message || 'Failed to change password',
        });
        throw err;
      }
    },
    [changePassword],
  );

  // Handler: Upgrade click
  const handleUpgradeClick = useCallback((): void => {
    setShowSubscriptionUpgrade(true);
  }, []);

  // Handler: Theme upgrade click
  const handleThemeUpgradeClick = useCallback((): void => {
    setShowThemeUpgrade(true);
  }, []);

  // Handler: Action message setter
  const handleActionMessage = useCallback(
    (message: { type: 'success' | 'error'; text: string }): void => {
      setActionMessage(message);
    },
    [],
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />

      {/* Panel */}
      <motion.div
        data-testid="settings-view"
        initial={{ opacity: 0, y: 20, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20, scale: 0.98 }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
        className={cn(
          'relative w-full max-w-2xl max-h-[90dvh] overflow-hidden border',
          'bg-theme-bg/95 backdrop-blur-sm rounded-2xl border-theme-border shadow-2xl shadow-black/40 mx-4',
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-theme-border">
          <h2 className="text-xl font-semibold text-theme-text">Settings</h2>
          <button
            type="button"
            data-testid="settings-close-button"
            onClick={onClose}
            className="p-2 hover:bg-theme-surface rounded-lg transition-colors"
            aria-label="Close settings"
          >
            <X className="w-5 h-5 text-theme-text-tertiary" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Action message: sticky, so a message from a section far down (password) stays in view */}
          {actionMessage && (
            <div className="sticky -top-4 z-10 -mt-4 pt-4 pb-4 bg-theme-bg">
              <div
                className={cn(
                  'p-3 rounded-lg text-sm border',
                  actionMessage.type === 'success'
                    ? 'bg-theme-success/10 border-theme-success/30 text-theme-success'
                    : 'bg-theme-danger/10 border-theme-danger/30 text-theme-danger',
                )}
              >
                {actionMessage.text}
                <button
                  type="button"
                  onClick={() => setActionMessage(null)}
                  className="ml-2 underline hover:no-underline"
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}

          {/* Sections */}
          <ProfileSection
            user={user}
            onSaveProfile={handleSaveProfile}
            onRequestEmailChange={requestEmailChange}
            onCancelEmailChange={cancelEmailChange}
          />

          <SubscriptionSection
            user={user}
            isPremium={isPremium}
            isPro={isPro}
            isElite={isElite}
            isFree={isFree}
            onUpgradeClick={handleUpgradeClick}
          />

          <QuotasSection isFree={isFree} onUpgradeClick={handleUpgradeClick} />

          <AppearanceSection
            themeId={themeId}
            themes={themes}
            user={user}
            canUseTheme={canUseTheme}
            onThemeChange={setTheme}
            onThemeUpgradeClick={handleThemeUpgradeClick}
            onUpdatePreferences={updatePreferences}
          />

          <PicksSection user={user} onUpdatePreferences={updatePreferences} />

          <NotificationsSection user={user} onUpdatePreferences={updatePreferences} />

          {!isLocked('odds_comparison') && (
            <LineShoppingSection user={user} onUpdatePreferences={updatePreferences} />
          )}

          <RelationshipPermissionsSection />

          <BlockedUsersSection />

          <CreatorEarningsSection onOpenEarningsDashboard={onOpenEarningsDashboard} />

          <SecuritySection onChangePassword={handleChangePassword} />

          <AccountPauseSection />

          <AccountDeletionSection />

          {/* Support Section */}
          <div className="bg-theme-surface rounded-xl p-6 border border-theme-border mb-6">
            <h2 className="text-lg font-semibold text-theme-text mb-4">Support</h2>
            <p className="text-sm text-theme-text-secondary mb-4">
              Need help? Have feedback or found an issue? We'd love to hear from you.
            </p>
            <div className="flex items-center gap-3">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-4 py-2 bg-theme-primary-fill hover:bg-theme-primary-fill-hover text-theme-on-primary-fill font-medium rounded-lg transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                Contact Support
              </Link>
              <a
                href="/faq"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 border border-theme-border hover:bg-theme-surface-hover text-theme-text font-medium rounded-lg transition-colors"
              >
                <HelpCircle className="w-4 h-4" />
                FAQ
              </a>
            </div>
          </div>

          <SharpSportsSection onActionMessage={handleActionMessage} />

          {/* Legal Section */}
          <section className="mb-6">
            <h3 className="text-lg font-medium text-theme-text mb-2">Legal</h3>
            <div className="bg-theme-surface rounded-lg border border-theme-border divide-y divide-theme-border">
              <button
                type="button"
                onClick={() => setLegalModalType('privacy')}
                className="flex items-center justify-between w-full p-4 hover:bg-theme-surface-hover transition-colors text-left"
              >
                <div className="flex items-center gap-3">
                  <Lock className="w-4 h-4 text-theme-text-secondary" />
                  <div>
                    <div className="text-sm font-medium text-theme-text">Privacy Policy</div>
                    <div className="text-xs text-theme-text-tertiary mt-0.5">
                      How we handle your data
                    </div>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-theme-text-tertiary" />
              </button>
              <button
                type="button"
                onClick={() => setLegalModalType('terms')}
                className="flex items-center justify-between w-full p-4 hover:bg-theme-surface-hover transition-colors text-left"
              >
                <div className="flex items-center gap-3">
                  <BookOpen className="w-4 h-4 text-theme-text-secondary" />
                  <div>
                    <div className="text-sm font-medium text-theme-text">Terms of Service</div>
                    <div className="text-xs text-theme-text-tertiary mt-0.5">
                      Rules and agreements
                    </div>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-theme-text-tertiary" />
              </button>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-theme-border flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-theme-bg hover:bg-theme-border text-theme-text font-medium rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </motion.div>

      {showThemeUpgrade && (
        <UpgradeModal
          mode="feature"
          feature="theme_premium"
          onDismiss={() => setShowThemeUpgrade(false)}
        />
      )}

      {showSubscriptionUpgrade && (
        <UpgradeModal
          mode="feature"
          feature={isPro ? 'history_community' : 'analytics_summary'}
          onDismiss={() => setShowSubscriptionUpgrade(false)}
        />
      )}

      {legalModalType && (
        <LegalModal type={legalModalType} onClose={() => setLegalModalType(null)} />
      )}
    </div>
  );
}
